import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress,
         Alert, TextField, Chip } from "@mui/material";
import { CheckCircle, Cancel, LinkOff, TaskAlt, OpenInNew } from "@mui/icons-material";
import { COLORS, TYPE, RADIUS, CONTROL_HEIGHT, humanizeStatus } from "../../../styles/recruiterTheme";
import PublicPageShell, {
  PublicCard, PublicTitle, PublicStatus, readJson, networkMessage,
} from "../PublicPageShell";

const BASE          = process.env.REACT_APP_API_BASE_URL;
const UPLOAD_RESUME = process.env.REACT_APP_RESUME_UPLOAD_AND_SEARCH_API;

const overline = { fontSize: TYPE.overline, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: COLORS.muted, mb: 0.5 };

export default function SeniorReviewPage() {
  const { token } = useParams();
  const [meta,     setMeta]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [loadError,setLoadError]= useState("");
  const [reloadKey,setReloadKey]= useState(0);
  const [feedback, setFeedback] = useState("");
  const [saving,   setSaving]   = useState(null); // decision in flight: "Accepted" | "Rejected" | null
  const [done,     setDone]     = useState(null); // "Accepted" | "Rejected"
  const [error,    setError]    = useState("");
  const [blobUrl,  setBlobUrl]  = useState(null);
  const [pdfState, setPdfState] = useState("loading"); // loading | ready | missing
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let url = null;
    setLoading(true); setLoadError(""); setPdfState("loading");
    fetch(`${BASE}/jd-review/public/review/${token}`)
      .then(readJson)
      .then(d => {
        if (cancelled) return;
        if (!d.success) throw new Error(d.message || "This review link is invalid or has expired.");
        setMeta(d);
      })
      .catch(e => { if (!cancelled) setLoadError(networkMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Load PDF — a 404 answers with JSON, so only treat a successful PDF response as the file.
    fetch(`${BASE}/jd-review/public/review/${token}/file`)
      .then(r => { if (!r.ok) throw new Error("missing"); return r.blob(); })
      .then(b => {
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setBlobUrl(url); setPdfState("ready");
      })
      .catch(() => { if (!cancelled) setPdfState("missing"); });

    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [token, reloadKey]);

  const decide = async (decision) => {
    if (inFlight.current) return;
    if (decision === "Rejected" && !feedback.trim()) {
      setError("Please provide feedback before rejecting."); return;
    }
    inFlight.current = true;
    setSaving(decision); setError("");
    try {
      const res = await fetch(`${BASE}/jd-review/public/review/${token}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, feedback: feedback.trim() }),
      }).then(readJson);
      if (!res.success) throw new Error(res.message || "Could not save your decision.");

      if (decision === "Accepted") {
        try {
          const resumeRes = await fetch(`${BASE}/jd-review/public/review/${token}/file`);
          if (!resumeRes.ok) throw new Error(`resume file HTTP ${resumeRes.status}`);
          const resumeBlob = await resumeRes.blob();
          // Same name as the profile placeholder's resumeUrl, so the model scores that profile
          // (a shared "resume.pdf" made every accepted candidate on a JD collide).
          const resumeFile = new File([resumeBlob], meta?.resume_file || `review-${token}.pdf`, { type: "application/pdf" });

          const fd = new FormData();
          fd.append("jd._id", meta?.jd_object_id || "");
          fd.append("job_description", meta?.job_description || "");
          fd.append("skills", Array.isArray(meta?.skills_required)
            ? meta.skills_required.join(", ")
            : (meta?.skills_required || ""));
          fd.append("experience", "");
          fd.append("jdID", meta?.jdID || "");
          fd.append("job_role", meta?.job_title || "");
          fd.append("recruiter_email", meta?.assigned_by || "");
          fd.append("recruiterid", "");
          fd.append("files", resumeFile);

          await fetch(UPLOAD_RESUME, { method: "POST", body: fd }).then(r => r.json());
        } catch (searchErr) {
          console.warn("Resume upload & search skipped:", searchErr.message);
        }
      }

      setDone(decision);
    } catch(e) { setError(networkMessage(e)); }
    finally { setSaving(null); inFlight.current = false; }
  };

  let body;
  if (loading) {
    body = <PublicStatus loading message="Loading the review…" />;
  } else if (loadError) {
    const network = /reach the server|HTTP 5/.test(loadError);
    body = (
      <PublicStatus tone="error" icon={<LinkOff />} title={network ? "Could not load this page" : "Link unavailable"}
        message={loadError}
        action={network ? { label: "Try again", onClick: () => setReloadKey(k => k + 1) } : null} />
    );
  } else if (done) {
    body = done === "Accepted"
      ? <PublicStatus tone="success" icon={<CheckCircle />} title="Resume accepted" message="The recruiter has been notified. You can close this page." />
      : <PublicStatus tone="error" icon={<Cancel />} title="Resume rejected" message="The candidate has been notified with your feedback. You can close this page." />;
  } else if (meta?.status && meta.status !== "Pending Review") {
    // Link opened again after a decision (or before the candidate re-uploaded).
    body = (
      <PublicStatus tone="muted" icon={<TaskAlt />} title="Nothing to review"
        message={`This resume is currently "${humanizeStatus(meta.status)}", so no decision is needed from this link.`} />
    );
  } else {
    const skills = Array.isArray(meta?.skills_required)
      ? meta.skills_required
      : String(meta?.skills_required || "").split(",").map(s => s.trim()).filter(Boolean);
    body = (
      <>
        <PublicTitle title={`Reviewing: ${meta?.candidate_name || "Candidate"}`}
          subtitle="Check the tailored resume against the job description, then accept it or send feedback to the candidate." />

        <PublicCard>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={meta?.job_description || skills.length ? 2 : 0}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.ink, wordBreak: "break-word" }}>
              {meta?.job_title || "Role"}
            </Typography>
            {meta?.client_name && (
              <Chip label={meta.client_name} size="small" sx={{ bgcolor: COLORS.accentSoft, color: COLORS.primaryDark }} />
            )}
          </Box>
          {meta?.job_description && (
            <>
              <Typography sx={overline}>Job description</Typography>
              <Typography sx={{ fontSize: TYPE.small, whiteSpace: "pre-wrap", wordBreak: "break-word", color: COLORS.ink, maxHeight: 240, overflowY: "auto" }}>
                {meta.job_description}
              </Typography>
            </>
          )}
          {skills.length > 0 && (
            <Box mt={2}>
              <Typography sx={overline}>Required skills</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {skills.map((s, i) => (
                  <Chip key={i} label={s} size="small" sx={{ bgcolor: COLORS.surfaceTint, color: COLORS.primaryDark, border: `1px solid ${COLORS.border}` }} />
                ))}
              </Box>
            </Box>
          )}
        </PublicCard>

        <PublicCard>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.ink }}>Tailored resume</Typography>
            {pdfState === "ready" && (
              <Button size="small" href={blobUrl} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNew />}
                sx={{ textTransform: "none", fontWeight: 600 }}>
                Open in new tab
              </Button>
            )}
          </Box>
          {pdfState === "ready" ? (
            <Box sx={{ height: { xs: 420, sm: 600 }, border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.sm}px`, overflow: "hidden" }}>
              <iframe src={blobUrl} title="Candidate Resume"
                style={{ width: "100%", height: "100%", border: "none" }} />
            </Box>
          ) : pdfState === "loading" ? (
            <Box py={4} textAlign="center"><CircularProgress size={24} /></Box>
          ) : (
            <Alert severity="warning">The resume PDF could not be loaded. You can still record a decision, or try reopening the link later.</Alert>
          )}
        </PublicCard>

        <PublicCard>
          <TextField fullWidth multiline minRows={3}
            label="Feedback (required if rejecting)"
            placeholder="E.g. Missing cloud experience, please add AWS/Azure projects…"
            value={feedback} onChange={e => { setFeedback(e.target.value); if (error) setError(""); }}
            disabled={!!saving} sx={{ mb: 2 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" gap={1.5} justifyContent="flex-end" flexWrap="wrap">
            <Button variant="outlined" color="error"
              onClick={() => decide("Rejected")} disabled={!!saving}
              startIcon={saving === "Rejected" ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
              sx={{ height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
              {saving === "Rejected" ? "Sending…" : "Reject & send feedback"}
            </Button>
            <Button variant="contained" color="success"
              onClick={() => decide("Accepted")} disabled={!!saving}
              startIcon={saving === "Accepted" ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              sx={{ height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
              {saving === "Accepted" ? "Saving…" : "Accept resume"}
            </Button>
          </Box>
        </PublicCard>
      </>
    );
  }

  return <PublicPageShell subtitle="Senior resume review">{body}</PublicPageShell>;
}
